import { trackAiChatBRM } from '@/common/sensorsTrackJM/trackAiChatBRM';
import slsLogger, { EVENT_TYPE } from '@/utils/slsLogger';
import { resolveResearchFileType } from '@/pages/aiAssistant/researchMode/utils/researchFileIcon';

/** sessionStorage key：待消费的 020-1-24 进宝模式入口来源（聊天 / 研究 / 任务共用） */
const RESEARCH_ENTRY_SOURCE_KEY = 'researchMode:entrySource';

/** 进入进宝模式（020-1-24）/ 配置页（020-14-5）的入口来源文案 */
export const RESEARCH_ENTRY_SOURCE = Object.freeze({
  AI_MENU: 'AI进宝菜单',
  MODE_TAB: '顶部模式Tab',
  DIRECT_LINK: '直接访问链接'
});

/** 020-1-24 入口来源文案别名（聊天 / 研究 / 任务三模式通用，语义与 RESEARCH_ENTRY_SOURCE 一致） */
export const MODE_ENTRY_SOURCE = RESEARCH_ENTRY_SOURCE;

/** 020-1-24 目标模式文案：区分本次进入的是哪个进宝模式 */
export const MODE_TARGET = Object.freeze({
  CHAT: '聊天模式',
  RESEARCH: '研究模式',
  TASK: '任务模式'
});

/** 020-14-5 配置页入口来源（与研究模式内交互场景对齐） */
export const CONFIG_PAGE_ENTRY_SOURCE = Object.freeze({
  LEFT_TOOLS: '左侧工具与连接',
  SKILL_BAR_MORE: '右侧技能栏查看更多',
  DIRECT_LINK: '直接访问链接',
  MESSAGE_CHANNEL_BANNER: '消息渠道引导浮窗'
});

/** 左侧工具 id → 020-14-5 page_type */
export const TOOL_ID_TO_PAGE_TYPE = Object.freeze({
  skill: '技能管理页',
  database: 'MCP服务页',
  'cron-jobs': '定时任务页',
  channel: '消息频道页',
  'data-source': '数据库'
});

const CHAT_HOME_ROUTE_NAMES = new Set(['aiChatIndex', 'comeinAichatIndex', 'thirdpartyComeinAichatIndex']);

/**
 * 上报 sensorsTrackJM 并同步 slsLogger.behavior。
 * @param {string} statisticnum TRACK_MODULE_BRM 路径
 * @param {Record<string, unknown>} extra 业务字段
 * @param {string} [action=EVENT_TYPE.CLICK] slsLogger 行为类型
 * @returns {void}
 */
function trackWithBehavior(statisticnum, extra = {}, action = EVENT_TYPE.CLICK) {
  trackAiChatBRM(statisticnum, extra);
  slsLogger.behavior({
    action,
    target: { statisticnum, ...extra }
  });
}

/**
 * 写入待消费的 020-1-24 入口来源（顶部模式 Tab 切换、左侧菜单等场景在路由跳转前调用）。
 * @param {string} entrySource MODE_ENTRY_SOURCE 中的值
 * @returns {void}
 */
export function setModeEntrySource(entrySource) {
  try {
    sessionStorage.setItem(RESEARCH_ENTRY_SOURCE_KEY, entrySource);
  } catch {
    /* 隐私模式等写入失败时忽略 */
  }
}

/** @deprecated 使用 setModeEntrySource；保留别名以兼容既有路由守卫引用 */
export const setResearchEntrySource = setModeEntrySource;

/**
 * 根据来源路由推断 020-1-24 入口来源；若 sessionStorage 已有值则优先保留。
 * @param {import('vue-router').RouteLocationNormalized|null|undefined} from
 * @returns {string}
 */
export function resolveModeEntrySourceFromRoute(from) {
  try {
    const pending = sessionStorage.getItem(RESEARCH_ENTRY_SOURCE_KEY);
    if (pending) return pending;
  } catch {
    /* ignore */
  }
  const fromName = from?.name || '';
  // 从进宝聊天首页跳转进入：视为经由 AI 进宝菜单入口
  if (CHAT_HOME_ROUTE_NAMES.has(fromName)) {
    return MODE_ENTRY_SOURCE.AI_MENU;
  }
  return MODE_ENTRY_SOURCE.DIRECT_LINK;
}

/** @deprecated 使用 resolveModeEntrySourceFromRoute；保留别名以兼容既有路由守卫引用 */
export const resolveResearchEntrySourceFromRoute = resolveModeEntrySourceFromRoute;

/**
 * 读取并清除 020-1-24 入口来源；缺省时视为直接访问链接。
 * @returns {string}
 */
export function consumeModeEntrySource() {
  try {
    const value = sessionStorage.getItem(RESEARCH_ENTRY_SOURCE_KEY);
    if (value) sessionStorage.removeItem(RESEARCH_ENTRY_SOURCE_KEY);
    return value || MODE_ENTRY_SOURCE.DIRECT_LINK;
  } catch {
    return MODE_ENTRY_SOURCE.DIRECT_LINK;
  }
}

/** @deprecated 使用 consumeModeEntrySource；保留别名以兼容既有页面引用 */
export const consumeResearchEntrySource = consumeModeEntrySource;

/**
 * 020-1-24：进入进宝任一模式页面，携带进入方式与目标模式。
 * @param {string} targetMode MODE_TARGET 中的目标模式文案
 * @param {string} entrySource MODE_ENTRY_SOURCE 中的入口来源文案
 * @returns {void}
 */
export function trackModeEnter(targetMode, entrySource) {
  trackWithBehavior(
    'AI_CHAT.child.HOME.child.ENTER_MODE',
    {
      entry_source: entrySource,
      target_mode: targetMode
    },
    EVENT_TYPE.NAVIGATE
  );
}

/**
 * 020-1-24：进入聊天模式页面。
 * @param {string} entrySource 入口来源
 * @returns {void}
 */
export function trackChatModeEnter(entrySource) {
  trackModeEnter(MODE_TARGET.CHAT, entrySource);
}

/**
 * 020-1-24：进入研究模式页面。
 * @param {string} entrySource 入口来源
 * @returns {void}
 */
export function trackResearchModeEnter(entrySource) {
  trackModeEnter(MODE_TARGET.RESEARCH, entrySource);
}

/** HTML 编辑器专项操作类型。 */
export const HTML_EDITOR_ACTION = Object.freeze({
  MODE_SWITCH: 'mode_switch',
  EDIT: 'edit',
  DELETE: 'delete',
  UNDO: 'undo',
  REDO: 'redo',
  AI_SEND: 'ai_send',
  SAVE_VERSION: 'save_version',
  HISTORY: 'history'
});

/**
 * HTML 编辑产品埋点路径（020-14-23 ~ 020-14-27）。
 * 基础信息（clickTime / userId / orgId）由 trackAiChatBRM 统一附带，业务侧只传 fileId。
 */
export const HTML_EDITOR_STATISTIC = Object.freeze({
  /** 020-14-23：AI 修改确定并生成批注 */
  AI_EDIT_CONFIRM: 'AI_CHAT.child.TASK_MODE_RESEARCH.child.HTML_AI_EDIT_CONFIRM',
  /** 020-14-24：人工修改确定 */
  MANUAL_EDIT_CONFIRM: 'AI_CHAT.child.TASK_MODE_RESEARCH.child.HTML_MANUAL_EDIT_CONFIRM',
  /** 020-14-25：点击撤销 */
  UNDO_CLICK: 'AI_CHAT.child.TASK_MODE_RESEARCH.child.HTML_UNDO_CLICK',
  /** 020-14-26：点击恢复 */
  REDO_CLICK: 'AI_CHAT.child.TASK_MODE_RESEARCH.child.HTML_REDO_CLICK',
  /** 020-14-27：点击清空批注 */
  CLEAR_ANNOTATIONS_CLICK: 'AI_CHAT.child.TASK_MODE_RESEARCH.child.HTML_CLEAR_ANNOTATIONS_CLICK'
});

/**
 * HTML 编辑产品埋点：只上报文件 ID，不附带其它业务字段。
 * @param {string} statisticnum HTML_EDITOR_STATISTIC 中的路径
 * @param {string|number} [fileId=''] 文件 / 文档节点 ID
 * @returns {void}
 */
function trackHtmlEditorProductStat(statisticnum, fileId = '') {
  trackWithBehavior(statisticnum, {
    fileId: String(fileId || '')
  });
}

/**
 * 020-14-23：HTML 编辑 AI 修改确定并生成批注。
 * @param {string|number} [fileId='']
 * @returns {void}
 */
export function trackHtmlAiEditConfirm(fileId = '') {
  trackHtmlEditorProductStat(HTML_EDITOR_STATISTIC.AI_EDIT_CONFIRM, fileId);
}

/**
 * 020-14-24：HTML 编辑人工修改确定。
 * @param {string|number} [fileId='']
 * @returns {void}
 */
export function trackHtmlManualEditConfirm(fileId = '') {
  trackHtmlEditorProductStat(HTML_EDITOR_STATISTIC.MANUAL_EDIT_CONFIRM, fileId);
}

/**
 * 020-14-25：HTML 编辑点击撤销。
 * @param {string|number} [fileId='']
 * @returns {void}
 */
export function trackHtmlUndoClick(fileId = '') {
  trackHtmlEditorProductStat(HTML_EDITOR_STATISTIC.UNDO_CLICK, fileId);
}

/**
 * 020-14-26：HTML 编辑点击恢复。
 * @param {string|number} [fileId='']
 * @returns {void}
 */
export function trackHtmlRedoClick(fileId = '') {
  trackHtmlEditorProductStat(HTML_EDITOR_STATISTIC.REDO_CLICK, fileId);
}

/**
 * 020-14-27：HTML 编辑点击清空批注。
 * @param {string|number} [fileId='']
 * @returns {void}
 */
export function trackHtmlClearAnnotationsClick(fileId = '') {
  trackHtmlEditorProductStat(HTML_EDITOR_STATISTIC.CLEAR_ANNOTATIONS_CLICK, fileId);
}

/**
 * HTML 编辑器内部操作日志（模式切换 / 落库 / 历史等）。
 * 不走 sensorsTrackJM：`HTML_EDITOR_ACTION` 未在 TRACK_MODULE_BRM 注册，解析会得到 undefined。
 * 产品埋点请用 trackHtmlAiEditConfirm / trackHtmlManualEditConfirm 等专用方法。
 * @param {{ action: string, docNodeId?: string|number, detail?: string }} payload - 操作上下文
 * @returns {void}
 */
export function trackHtmlEditorAction({ action, docNodeId = '', detail = '' }) {
  slsLogger.console({
    component: 'ResearchHtmlEditor',
    level: 'info',
    message: '研究模式 HTML 编辑器操作',
    extra: {
      action,
      docNodeId: String(docNodeId || ''),
      detail
    }
  });
}

/**
 * 020-1-24：进入任务模式页面。
 * @param {string} entrySource 入口来源
 * @returns {void}
 */
export function trackTaskModeEnter(entrySource) {
  trackModeEnter(MODE_TARGET.TASK, entrySource);
}

/**
 * 020-14-5：访问配置功能页面。
 * @param {{ entrySource: string, pageType: string }} payload
 * @returns {void}
 */
export function trackResearchConfigPageVisit({ entrySource, pageType }) {
  trackWithBehavior(
    'AI_CHAT.child.TASK_MODE_RESEARCH.child.CONFIG_PAGE_VISIT',
    {
      entry_source: entrySource,
      page_type: pageType
    },
    EVENT_TYPE.NAVIGATE
  );
}

/**
 * 020-14-6：右侧常用技能栏技能点击。
 * @param {string} skillEntryName 技能入口名称（含「更多」）
 * @returns {void}
 */
export function trackResearchSkillShortcutClick(skillEntryName) {
  trackWithBehavior('AI_CHAT.child.TASK_MODE_RESEARCH.child.SKILL_SHORTCUT_BAR_CLICK', {
    skillEntryName
  });
}

/**
 * 解析技能类型文案（官方 / 用户）。
 * @param {object|null|undefined} skill
 * @returns {string}
 */
function resolveSkillTypeLabel(skill) {
  const source = String(skill?.source || skill?.sourceType || '').toLowerCase();
  if (source === 'builtin') return '官方技能';
  return '用户技能';
}

/**
 * 020-14-7：技能详情弹窗「对话使用」。
 * @param {object|null|undefined} skill
 * @returns {void}
 */
export function trackResearchSkillDetailUseInConversation(skill) {
  if (!skill) return;
  const skillName = skill.displayName || skill.label || skill.name || '';
  trackWithBehavior('AI_CHAT.child.TASK_MODE_RESEARCH.child.SKILL_DETAIL_USE_IN_CONVERSATION', {
    skillId: skill.workspaceSkillId || skill.name || skill.id || '',
    skillName,
    skillType: resolveSkillTypeLabel(skill)
  });
}

/**
 * 020-14-9：技能页「创建技能」-「对话创建」。
 * @returns {void}
 */
export function trackResearchCreateSkillByConversation() {
  trackWithBehavior('AI_CHAT.child.TASK_MODE_RESEARCH.child.CREATE_SKILL_BY_CONVERSATION');
}

/**
 * 020-14-14：文档页相关 AI 会话入口/浮窗展示。
 * @param {{ docId: string|number, docType?: string|number, autoShowPopover: 0|1 }} payload
 * @returns {void}
 */
export function trackRelatedAiSessionsView({ docId, docType, autoShowPopover }) {
  trackWithBehavior(
    'AI_CHAT.child.TASK_MODE_RESEARCH.child.RELATED_AI_SESSIONS_VIEW',
    {
      docId,
      docType: docType ?? '',
      autoShowPopover
    },
    EVENT_TYPE.NAVIGATE
  );
}

/**
 * 020-14-15：文档页相关 AI 会话浮窗点击。
 * @param {{ docId: string|number, docType?: string|number, sessionId?: string, clickObject: 1|2 }} payload
 * @returns {void}
 */
export function trackRelatedAiSessionsClick({ docId, docType, sessionId = '', clickObject }) {
  trackWithBehavior('AI_CHAT.child.TASK_MODE_RESEARCH.child.RELATED_AI_SESSIONS_CLICK', {
    docId,
    docType: docType ?? '',
    sessionId,
    clickObject
  });
}

/**
 * 020-14-16 历史关联文档筛选入口来源：
 * 1=点击「关联文档」展开下拉；2=点击下拉选项进入筛选态。
 */
export const HISTORY_RELATED_DOC_FILTER_ENTRY = Object.freeze({
  OPEN_DROPDOWN: 1,
  SELECT_OPTION: 2
});

/**
 * 020-14-16：历史记录页触发关联文档筛选。
 * 基础信息（clickTime / userId / orgId）由 trackAiChatBRM 统一附带。
 * @param {{ docId?: string|number, entrySource: 1|2, sessionCount?: number }} payload
 * @returns {void}
 */
export function trackHistoryRelatedDocFilter({ docId = '', entrySource, sessionCount = 0 }) {
  if (!entrySource) return;
  trackWithBehavior(
    'AI_CHAT.child.TASK_MODE_RESEARCH.child.HISTORY_RELATED_DOC_FILTER',
    {
      docId: docId ? String(docId) : '',
      entrySource,
      sessionCount: Number(sessionCount) || 0
    },
    EVENT_TYPE.FILTER
  );
}

/**
 * 020-14-28：历史记录面板点击会话置顶 / 取消置顶。
 * 基础信息（clickTime / userId / orgId）由 trackAiChatBRM 统一附带。
 * @param {{ sessionId: string, toPin: 0|1 }} payload toPin=1 置顶，0 取消置顶
 * @returns {void}
 */
export function trackHistorySessionPin({ sessionId, toPin }) {
  trackWithBehavior('AI_CHAT.child.TASK_MODE_RESEARCH.child.HISTORY_SESSION_PIN', {
    sessionId: sessionId ? String(sessionId) : '',
    toPin
  });
}

/** 020-14-32 收藏按钮位置：1=资料库列表，2=文档顶部 */
export const FAVORITE_BUTTON_LOCATION = Object.freeze({
  LIBRARY_LIST: 1,
  DOCUMENT_TOP: 2
});

/** 020-14-32 点击后的收藏状态：1=收藏，2=取消收藏 */
export const FAVORITE_BUTTON_STATUS = Object.freeze({
  FAVORITE: 1,
  UNFAVORITE: 2
});

/** 收藏 / 最近浏览产品埋点路径（020-14-32 ~ 020-14-34）。 */
export const FAVORITE_RECENT_STATISTIC = Object.freeze({
  /** 020-14-32：点击收藏按钮 */
  BUTTON_CLICK: 'AI_CHAT.child.TASK_MODE_RESEARCH.child.FAVORITE_BUTTON_CLICK',
  /** 020-14-33：点击收藏列表任一文件 */
  FAVORITE_LIST_FILE_CLICK: 'AI_CHAT.child.TASK_MODE_RESEARCH.child.FAVORITE_LIST_FILE_CLICK',
  /** 020-14-34：点击最近浏览列表任一文件 */
  RECENT_LIST_FILE_CLICK: 'AI_CHAT.child.TASK_MODE_RESEARCH.child.RECENT_LIST_FILE_CLICK'
});

/**
 * 020-14-32：点击收藏按钮。
 * 基础信息（clickTime / userId / orgId）由 trackAiChatBRM 统一附带。
 * @param {{ location: 1|2, favoriteStatus: 1|2 }} payload location=1 资料库列表，2 文档顶部；favoriteStatus=1 收藏，2 取消收藏
 * @returns {void}
 */
export function trackFavoriteButtonClick({ location, favoriteStatus }) {
  trackWithBehavior(FAVORITE_RECENT_STATISTIC.BUTTON_CLICK, {
    location,
    favoriteStatus
  });
}

/**
 * 将收藏入口来源映射为 020-14-32 的位置。
 * @param {string} [source] library / favorite / recent / editor
 * @returns {1|2}
 */
export function resolveFavoriteButtonLocation(source) {
  // 文档顶部工具栏单独记为位置 2，其余资料库列表入口均为位置 1
  if (source === 'editor') {
    return FAVORITE_BUTTON_LOCATION.DOCUMENT_TOP;
  }
  return FAVORITE_BUTTON_LOCATION.LIBRARY_LIST;
}

/**
 * 020-14-33：点击收藏列表任一文件。
 * @param {string|number} [fileId=''] 文件 / 文档节点 ID
 * @returns {void}
 */
export function trackFavoriteListFileClick(fileId = '') {
  trackWithBehavior(FAVORITE_RECENT_STATISTIC.FAVORITE_LIST_FILE_CLICK, {
    fileId: String(fileId || '')
  });
}

/**
 * 020-14-34：点击最近浏览列表任一文件。
 * @param {string|number} [fileId=''] 文件 / 文档节点 ID
 * @returns {void}
 */
export function trackRecentListFileClick(fileId = '') {
  trackWithBehavior(FAVORITE_RECENT_STATISTIC.RECENT_LIST_FILE_CLICK, {
    fileId: String(fileId || '')
  });
}

/**
 * 分享预览页埋点路径（统一注册于 trackModuleBRM.RESEARCH_DOCUMENT_SHARE，解析为 80-14-x）。
 * PAGE_VIEW：查看分享预览页面（80-14-1）；PAGE_DOWNLOAD：预览页确认下载（80-14-2）。
 */
export const SHARE_PREVIEW_STATISTIC = Object.freeze({
  VIEW: 'RESEARCH_DOCUMENT_SHARE.child.PREVIEW.child.PAGE_VIEW',
  DOWNLOAD: 'RESEARCH_DOCUMENT_SHARE.child.PREVIEW.child.PAGE_DOWNLOAD'
});

/**
 * 将分享详情 / 接口字段转为目录树同源类型字符串（md / word / excel / ppt 等）。
 * 复用 resolveResearchFileType，入参对齐左侧资料库：ext + editorEngine。
 * @param {object|null|undefined} detail 分享详情（归一化后或接口原始字段均可）
 * @returns {string} 文件类型文案；无详情时返回空字符串
 * @example
 * // 接口：{ fileExt: 'docx', editorEngine: 4, previewType: 'ONLYOFFICE', ... }
 * resolveSharePreviewTrackFileType(detail) // 'word'
 */
export function resolveSharePreviewTrackFileType(detail) {
  if (!detail) return '';

  // 兼容归一化字段 ext 与接口原始 fileExt
  return resolveResearchFileType({
    ext: detail.ext || detail.fileExt,
    editorEngine: detail.editorEngine
  });
}

/**
 * 80-14-1：进入研究模式分享预览页面即上报。
 * 基础信息（访问时间 clickTime / 用户PID userId / 机构ID orgId）由 trackAiChatBRM 统一附带。
 * @param {string} [fileType=''] 文件类型（md / word / excel / ppt 等）
 * @returns {void}
 */
export function trackSharePreviewPageView(fileType = '') {
  trackWithBehavior(
    SHARE_PREVIEW_STATISTIC.VIEW,
    {
      fileType: fileType || ''
    },
    EVENT_TYPE.NAVIGATE
  );
}

/**
 * 80-14-2：分享预览页确认执行下载时上报。
 * 基础信息（点击时间 clickTime / 用户PID userId / 机构ID orgId）由 trackAiChatBRM 统一附带。
 * @param {string} [fileType=''] 文件类型（md / word / excel / ppt 等）
 * @returns {void}
 */
export function trackSharePreviewPageDownload(fileType = '') {
  trackWithBehavior(
    SHARE_PREVIEW_STATISTIC.DOWNLOAD,
    {
      fileType: fileType || ''
    },
    EVENT_TYPE.DOWNLOAD
  );
}
